import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/** Modal props shared by all inventory filter drawers — avoids expensive scroll-lock on heavy pages. */
const FILTER_DRAWER_MODAL_PROPS = { disableScrollLock: true };
const FILTER_DRAWER_SLIDE_PROPS = { timeout: { enter: 180, exit: 140 } };

/**
 * Shared filter drawer shell used by CertifiedInventory and LotsInventory.
 *
 * Props:
 *   open        – boolean
 *   onClose     – () => void
 *   title       – string
 *   children    – filter field JSX (mounted after first paint for speed)
 *   actions     – footer button JSX  (Search, Done, etc.)
 *   formProps   – extra props spread on the inner Box (e.g. component="form", onSubmit)
 */
const FilterDrawer = ({ open, onClose, title, children, actions, formProps = {} }) => {
  const [bodyReady, setBodyReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setBodyReady(false);
      return undefined;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setBodyReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={FILTER_DRAWER_MODAL_PROPS}
      SlideProps={FILTER_DRAWER_SLIDE_PROPS}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, maxWidth: '100vw' } }}
    >
      <Box {...formProps} sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...(formProps.sx || {}) }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" component="span">{title}</Typography>
          <IconButton edge="end" onClick={onClose} aria-label="Close filters">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable body — deferred by double-rAF so the drawer shell paints first */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2, minHeight: 0 }}>
          {!bodyReady ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }} aria-busy="true">
              <CircularProgress size={32} />
            </Box>
          ) : (
            children
          )}
        </Box>

        {/* Sticky footer */}
        {actions && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, p: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}>
            {actions}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
